import { useState, useEffect } from "react";
import { X, Share, MoreVertical, Plus, Smartphone, Monitor } from "lucide-react";
import { apiClient } from "@/App";
import { tokens } from "@/design-system/tokens";

const detectPlatform = () => {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  if (isIOS && isSafari) return "ios";
  if (isAndroid) return "android";
  return "desktop";
};

export function InstallPrompt({ onDismiss }) {
  const [platform] = useState(detectPlatform);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const hostUrl = "https://fretless.life";

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const markSeenAndDismiss = async () => {
    try {
      await apiClient.post("/user/seen-install-prompt");
    } catch {}
    onDismiss();
  };

  const handleAndroidInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
    markSeenAndDismiss();
  };

  const Step = ({ num, icon, text }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: `${tokens.space.xl}px`, paddingTop: `${tokens.space.md}px`, paddingBottom: `${tokens.space.md}px` }}>
      <div style={{
        flexShrink: 0,
        width: "32px",
        height: "32px",
        borderRadius: "50%",
        backgroundColor: "rgba(245,137,31,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: 700,
        color: tokens.color.orange
      }}>
        {num}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px`, fontSize: "12px", color: tokens.color.textMuted }}>
        {icon}
        <span>{text}</span>
      </div>
    </div>
  );

  if (platform === "ios") {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 200,
          backgroundColor: "rgba(0,0,0,0.9)",
          backdropFilter: "blur(12px)",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          animation: "fadeIn 300ms ease-out"
        }}
        role="dialog"
        aria-label="Install prompt"
        data-testid="install-prompt-ios"
      >
        <div style={{
          width: "100%",
          maxWidth: "448px",
          backgroundColor: tokens.color.cardBg,
          border: `1px solid ${tokens.color.innerBorder}`,
          borderRadius: "32px 32px 0 0",
          padding: `${tokens.space.xxl}px`,
          position: "relative"
        }}>
          <button
            onClick={markSeenAndDismiss}
            style={{
              position: "absolute",
              top: `${tokens.space.lg}px`,
              right: `${tokens.space.lg}px`,
              color: tokens.color.textMuted,
              background: "none",
              border: "none",
              cursor: "pointer",
              transition: "color 150ms ease"
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
            onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
            aria-label="Close"
          >
            <X style={{ height: "20px", width: "20px" }} />
          </button>
          <h2 style={{ fontSize: "20px", fontWeight: 700, color: tokens.color.text, marginBottom: `${tokens.space.xs}px`, fontFamily: "Fraunces, serif", margin: 0 }}>Add Fretless to your Home Screen</h2>
          <p style={{ color: tokens.color.textMuted, fontSize: "12px", marginBottom: `${tokens.space.xxl}px`, margin: 0, marginTop: `${tokens.space.md}px` }}>Use it like an app — no app store needed</p>
          <div style={{ borderTop: `1px solid ${tokens.color.innerBorder}` }}>
            <Step num={1} icon={<Share style={{ height: "16px", width: "16px", color: tokens.color.orange }} />} text="Tap the Share icon (square with arrow) at the bottom of Safari" />
            <div style={{ borderTop: `1px solid ${tokens.color.innerBorder}` }} />
            <Step num={2} icon={<Plus style={{ height: "16px", width: "16px", color: tokens.color.orange }} />} text='Scroll down and tap "Add to Home Screen"' />
            <div style={{ borderTop: `1px solid ${tokens.color.innerBorder}` }} />
            <Step num={3} icon={<Smartphone style={{ height: "16px", width: "16px", color: tokens.color.orange }} />} text='Tap "Add" in the top right' />
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: `${tokens.space.lg}px` }} aria-hidden="true">
            <div style={{ animation: "bounce 1s infinite" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: tokens.color.orange }}>
                <path d="M12 4v14m0 0l-5-5m5 5l5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <button
            onClick={markSeenAndDismiss}
            style={{
              backgroundColor: tokens.color.orange,
              color: tokens.color.text,
              border: "none",
              borderRadius: tokens.radius.md,
              padding: "6px 12px",
              fontWeight: 500,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s",
              opacity: 1,
              width: "100%",
              marginTop: `${tokens.space.xxl}px`
            }}
            data-testid="install-dismiss-btn"
          >
            Got it
          </button>
        </div>
        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        `}</style>
      </div>
    );
  }

  if (platform === "android") {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 200,
          backgroundColor: "rgba(0,0,0,0.9)",
          backdropFilter: "blur(12px)",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          animation: "fadeIn 300ms ease-out"
        }}
        role="dialog"
        aria-label="Install prompt"
        data-testid="install-prompt-android"
      >
        <div style={{
          width: "100%",
          maxWidth: "448px",
          backgroundColor: tokens.color.cardBg,
          border: `1px solid ${tokens.color.innerBorder}`,
          borderRadius: "32px 32px 0 0",
          padding: `${tokens.space.xxl}px`,
          position: "relative"
        }}>
          <button
            onClick={markSeenAndDismiss}
            style={{
              position: "absolute",
              top: `${tokens.space.lg}px`,
              right: `${tokens.space.lg}px`,
              color: tokens.color.textMuted,
              background: "none",
              border: "none",
              cursor: "pointer",
              transition: "color 150ms ease"
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
            onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
            aria-label="Close"
          >
            <X style={{ height: "20px", width: "20px" }} />
          </button>
          <h2 style={{ fontSize: "20px", fontWeight: 700, color: tokens.color.text, marginBottom: `${tokens.space.xs}px`, fontFamily: "Fraunces, serif", margin: 0 }}>Install Fretless like an app</h2>
          <p style={{ color: tokens.color.textMuted, fontSize: "12px", marginBottom: `${tokens.space.xxl}px`, margin: 0, marginTop: `${tokens.space.md}px` }}>Quick access from your home screen.</p>
          {deferredPrompt ? (
            <button
              onClick={handleAndroidInstall}
              style={{
                backgroundColor: tokens.color.orange,
                color: tokens.color.text,
                border: "none",
                borderRadius: tokens.radius.md,
                padding: "6px 12px",
                fontWeight: 500,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s",
                opacity: 1,
                width: "100%"
              }}
              data-testid="install-native-btn"
            >
              Install Now
            </button>
          ) : (
            <>
              <div style={{ borderTop: `1px solid ${tokens.color.innerBorder}` }}>
                <Step num={1} icon={<MoreVertical style={{ height: "16px", width: "16px", color: tokens.color.orange }} />} text="Tap the three-dot menu (top right of Chrome)" />
                <div style={{ borderTop: `1px solid ${tokens.color.innerBorder}` }} />
                <Step num={2} icon={<Plus style={{ height: "16px", width: "16px", color: tokens.color.orange }} />} text='Tap "Add to Home screen"' />
                <div style={{ borderTop: `1px solid ${tokens.color.innerBorder}` }} />
                <Step num={3} icon={<Smartphone style={{ height: "16px", width: "16px", color: tokens.color.orange }} />} text='Tap "Install"' />
              </div>
              <button
                onClick={markSeenAndDismiss}
                style={{
                  backgroundColor: tokens.color.orange,
                  color: tokens.color.text,
                  border: "none",
                  borderRadius: tokens.radius.md,
                  padding: "6px 12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.2s",
                  opacity: 1,
                  width: "100%",
                  marginTop: `${tokens.space.xxl}px`
                }}
                data-testid="install-dismiss-btn"
              >
                Done
              </button>
            </>
          )}
        </div>
        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // Desktop — QR code modal
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        backgroundColor: "rgba(0,0,0,0.8)",
        backdropFilter: "blur(12px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: `${tokens.space.lg}px`,
        animation: "fadeIn 300ms ease-out"
      }}
      role="dialog"
      aria-label="Install prompt"
      data-testid="install-prompt-desktop"
    >
      <div style={{
        maxWidth: "448px",
        width: "100%",
        backgroundColor: tokens.color.cardBg,
        border: `1px solid ${tokens.color.innerBorder}`,
        borderRadius: tokens.radius.xl,
        padding: "32px",
        textAlign: "center",
        position: "relative"
      }}>
        <button
          onClick={markSeenAndDismiss}
          style={{
            position: "absolute",
            top: `${tokens.space.lg}px`,
            right: `${tokens.space.lg}px`,
            color: tokens.color.textMuted,
            background: "none",
            border: "none",
            cursor: "pointer",
            transition: "color 150ms ease"
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
          onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
          aria-label="Close"
        >
          <X style={{ height: "20px", width: "20px" }} />
        </button>
        <Monitor style={{ height: "40px", width: "40px", color: tokens.color.orange, margin: "0 auto 16px auto" }} />
        <h2 style={{ fontSize: "20px", fontWeight: 700, color: tokens.color.text, marginBottom: `${tokens.space.md}px`, fontFamily: "Fraunces, serif", margin: 0, marginTop: 0 }}>Install on your phone</h2>
        <p style={{ color: tokens.color.textMuted, fontSize: "12px", marginBottom: `${tokens.space.xxl}px`, margin: 0, marginTop: `${tokens.space.md}px` }}>Scan to install Fretless on your mobile device</p>
        <div style={{
          backgroundColor: "white",
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space.lg}px`,
          display: "inline-block",
          marginBottom: `${tokens.space.md}px`
        }}>
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(hostUrl)}&bgcolor=FFFFFF&color=0D0D0D`}
            alt="QR code to install Fretless"
            style={{ width: "192px", height: "192px" }}
            data-testid="install-qr-code"
          />
        </div>
        <p style={{ fontSize: "10px", color: tokens.color.textMuted, marginBottom: `${tokens.space.md}px`, margin: 0, wordBreak: "break-all", marginTop: `${tokens.space.md}px` }}>{hostUrl}</p>
        <button
          onClick={markSeenAndDismiss}
          style={{
            backgroundColor: tokens.color.orange,
            color: tokens.color.text,
            border: "none",
            borderRadius: tokens.radius.md,
            padding: "6px 12px",
            fontWeight: 500,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            transition: "all 0.2s",
            opacity: 1,
            width: "100%"
          }}
          data-testid="install-dismiss-btn"
        >
          Continue on desktop
        </button>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}
